import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

// Printable mailing label for a customer. Dave 2026-06-22 — replaces
// the Maximizer "Print Mailing Label" feature he was using. Opens in
// its own browser tab via window.open() from the customer detail
// page, so no sidebar / masthead chrome — just the printable sheet.
//
// Layout matches the Maximizer screenshot:
//   - Return address top-left (XSite Group HQ, fixed)
//   - Recipient block centered-horizontally, ~3 inches down
//
// Auto-fires window.print() once the customer data lands so the user
// goes straight from "click button" → browser print dialog.

const RETURN_ADDRESS = {
  name:    'XSite Group Inc.',
  line1:   '16-140 Welland Ave.',
  city:    'St. Catharines, ON',
  postal:  'L2R 2N6',
};

function joinNonEmpty(parts, sep = ' ') {
  return parts.filter((p) => p && String(p).trim()).join(sep);
}

export default function CustomerMailingLabel() {
  const { id } = useParams();
  const [customer, setCustomer] = useState(null);
  const [error, setError] = useState(null);
  const [printed, setPrinted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`/api/customers/${id}`)
      .then((r) => { if (!cancelled) setCustomer(r.data?.customer || r.data); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.error || e.message); });
    return () => { cancelled = true; };
  }, [id]);

  // Fire window.print() once the customer is loaded. Tiny delay so
  // the DOM has had a paint pass and the print preview shows the
  // actual label, not an empty page.
  useEffect(() => {
    if (!customer || printed) return;
    const t = setTimeout(() => {
      window.print();
      setPrinted(true);
    }, 300);
    return () => clearTimeout(t);
  }, [customer, printed]);

  if (error) {
    return <div style={{ padding: '2in', fontFamily: 'Arial, sans-serif' }}>Error: {error}</div>;
  }
  if (!customer) {
    return <div style={{ padding: '2in', fontFamily: 'Arial, sans-serif' }}>Loading…</div>;
  }

  // Recipient lines. Prefer the customer's contact-card name; fall
  // back to the company name if no first/last is set.
  const recipientName = joinNonEmpty([customer.first_name, customer.last_name]) || customer.company || '';
  // Mirror the Maximizer screenshot: "Contact - phone" on one line,
  // "Company - site_number" on the next.
  const phone = customer.phone_main || customer.phone_cell || '';
  const siteRef = customer.site_number || '';

  return (
    <>
      {/* Print-specific styles: letter page, no margins so the @page
          rule wins; everything else is positioned in `in` units to
          match the Maximizer layout out of the box. */}
      <style>{`
        @page { size: letter; margin: 0; }
        html, body { margin: 0; padding: 0; background: #fff; }
        body { font-family: Arial, Helvetica, sans-serif; }
        .label-sheet {
          width: 8.5in;
          height: 11in;
          position: relative;
          page-break-after: always;
          color: #000;
        }
        .return-address {
          position: absolute;
          top: 1in;
          left: 1in;
          font-size: 12pt;
          line-height: 1.35;
        }
        .recipient {
          position: absolute;
          top: 3.2in;
          left: 3in;
          font-size: 14pt;
          line-height: 1.5;
        }
        .recipient .spacer { height: 0.4in; }
        .label-toolbar {
          position: fixed;
          top: 0; left: 0; right: 0;
          padding: 8px 16px;
          background: #1a202c;
          color: #fff;
          display: flex; gap: 12px; align-items: center;
          font-family: Arial, sans-serif; font-size: 13px;
          z-index: 100;
        }
        .label-toolbar button {
          background: #0e7490; color: #fff;
          border: 0; padding: 6px 14px; border-radius: 4px;
          cursor: pointer; font-size: 13px;
        }
        .label-toolbar button.secondary {
          background: transparent; border: 1px solid #cbd5e1;
        }
        @media print {
          .label-toolbar { display: none !important; }
          .label-sheet { width: auto; height: auto; }
        }
      `}</style>

      {/* Toolbar visible on-screen only — gone on print. Lets Dave
          re-trigger the dialog if he dismisses it accidentally. */}
      <div className="label-toolbar">
        <span>Mailing Label — {customer.company || recipientName}</span>
        <button onClick={() => window.print()}>Print</button>
        <button className="secondary" onClick={() => window.close()}>Close</button>
      </div>

      <div className="label-sheet">
        <div className="return-address">
          <div>{RETURN_ADDRESS.name}</div>
          <div>{RETURN_ADDRESS.line1}</div>
          <div>{RETURN_ADDRESS.city}</div>
          <div>{RETURN_ADDRESS.postal}</div>
        </div>

        <div className="recipient">
          {/* Line 1: contact - phone */}
          {joinNonEmpty([recipientName, phone], ' - ') && (
            <div>{joinNonEmpty([recipientName, phone], ' - ')}</div>
          )}
          {/* Spacer to match the Maximizer screenshot which had a blank
              line between the contact line and the company block. */}
          {recipientName && <div className="spacer" />}
          {/* Line 2: company - site number */}
          {customer.company && (
            <div>{joinNonEmpty([customer.company, siteRef], ' - ')}</div>
          )}
          {customer.address1 && <div>{customer.address1}</div>}
          {customer.address2 && <div>{customer.address2}</div>}
          {joinNonEmpty([customer.city, customer.province_state], ', ') && (
            <div>{joinNonEmpty([customer.city, customer.province_state], ', ')}</div>
          )}
          {joinNonEmpty([customer.country || 'Canada', customer.postal_zip], ', ') && (
            <div>{joinNonEmpty([customer.country || 'Canada', customer.postal_zip], ', ')}</div>
          )}
        </div>
      </div>
    </>
  );
}
